import { Show } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { useNavigate, useParams } from "react-router";
import {
  Card,
  Descriptions,
  Button,
  Space,
  Empty,
  Tag,
  Spin,
  Timeline,
  Row,
  Col,
  Image,
  Typography,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  HistoryOutlined,
  PictureOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  EditOutlined,
  CalendarOutlined,
  UserOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { AdjacentCropDetail, AdjacentCropStatus } from "../../types/adjacent-crop";
import dayjs from "dayjs";
import "./adjacent-crops.css";
import { usePageTitle } from "../../hooks";

const { Title, Text, Paragraph } = Typography;

/**
 * AdjacentCropShow Component
 * 
 * Displays detailed information about an adjacent crop including:
 * - General information (species, variety, farm, producer, geometry)
 * - Timeline of all reviews with audit dates
 * - Photos from all reviews
 * - Closure information if closed
 * - Map showing crop location
 * - Edit and close action buttons
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 7.2
 */
export const AdjacentCropShow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { query } = useShow<AdjacentCropDetail>({
    resource: "adjacent-crops",
    id,
  });

  const { data, isLoading } = query;
  const adjacentCrop = data?.data;
  
  // Set page title
  usePageTitle(adjacentCrop?.name || t("adjacent_crops.title", "Cultivo Adyacente"));

  const handleBack = () => {
    navigate("/adjacent-crops");
  };

  const handleEdit = () => {
    navigate(`/adjacent-crops/edit/${id}`);
  };

  const handleClose = () => {
    navigate(`/adjacent-crops/${id}/close`);
  };

  /**
   * Get status color
   */
  const getStatusColor = (status: AdjacentCropStatus): string => {
    return status === "open" ? "green" : "red";
  };

  /**
   * Get status text
   */
  const getStatusText = (status: AdjacentCropStatus): string => {
    return status === "open"
      ? t("adjacent_crops.status.open", "Abierto")
      : t("adjacent_crops.status.closed", "Cerrado");
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!adjacentCrop) {
    return (
      <Card>
        <Empty description={t("adjacent_crops.not_found", "Cultivo adyacente no encontrado")} />
      </Card>
    );
  }

  // Sort reviews chronologically (oldest first)
  const sortedReviews = adjacentCrop.reviews
    ? [...adjacentCrop.reviews].sort((a, b) => 
        new Date(a.audit_date).getTime() - new Date(b.audit_date).getTime()
      )
    : [];

  // Collect all photos from all reviews
  const allPhotos = sortedReviews.flatMap(review => 
    review.photos.map(photo => ({
      ...photo,
      audit_reference: review.audit_reference,
      audit_date: review.audit_date,
    }))
  );

  return (
    <div className="adjacent-crop-show">
      <Show
        isLoading={isLoading}
        headerButtons={() => (
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              {t("common.back", "Volver")}
            </Button>
            {adjacentCrop?.status === "open" && (
              <>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  {t("common.edit", "Editar")}
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleClose}
                >
                  {t("adjacent_crops.action.close", "Cerrar")}
                </Button>
              </>
            )}
          </Space>
        )}
        title={adjacentCrop.name}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* General Information */}
          <Card
            title={
              <Space>
                <InfoCircleOutlined />
                {t("adjacent_crops.general_info", "Información General")}
              </Space>
            }
          >
            <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
              <Descriptions.Item label={t("adjacent_crops.field.species", "Especie")}>
                <Text strong>{adjacentCrop.species_name}</Text>
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.variety", "Variedad")}>
                {adjacentCrop.variety_name || (
                  <Text type="secondary">
                    {t("adjacent_crops.variety.unknown", "Desconocida")}
                  </Text>
                )}
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.producer", "Productor")}>
                {adjacentCrop.producer_name}
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.farm", "Finca")}>
                {adjacentCrop.farm_name}
              </Descriptions.Item>

              {adjacentCrop.farm_municipality && (
                <Descriptions.Item label={t("adjacent_crops.field.municipality", "Municipio")}>
                  {adjacentCrop.farm_municipality}
                </Descriptions.Item>
              )}

              <Descriptions.Item label={t("adjacent_crops.field.is_producer_owner", "Propiedad del Productor")}>
                <Tag color={adjacentCrop.is_producer_owner ? "green" : "orange"}>
                  {adjacentCrop.is_producer_owner 
                    ? t("common.yes", "Sí") 
                    : t("common.no", "No")}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.detection_date", "Fecha Detección")}>
                {adjacentCrop.detection_date
                  ? dayjs(adjacentCrop.detection_date).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.status", "Estado")}>
                <Tag color={getStatusColor(adjacentCrop.status)}>
                  {getStatusText(adjacentCrop.status)}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.surface", "Superficie Estimada")}>
                {adjacentCrop.total_estimated_surface?.toFixed(2)} ha
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.review_count", "Nº Revisiones")}>
                {adjacentCrop.review_count}
              </Descriptions.Item>

              <Descriptions.Item label={t("adjacent_crops.field.photo_count", "Nº Fotos")}>
                {adjacentCrop.photo_count}
              </Descriptions.Item>
            </Descriptions>

            {/* Closure Information - Only shown when closed */}
            {adjacentCrop.status === "closed" && (
              <>
                <Divider />
                <Title level={5}>
                  {t("adjacent_crops.closure_info", "Información de Cierre")}
                </Title>
                <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
                  <Descriptions.Item label={t("adjacent_crops.field.closed_date", "Fecha de Cierre")}>
                    {adjacentCrop.closed_date
                      ? dayjs(adjacentCrop.closed_date).format("DD/MM/YYYY")
                      : "-"}
                  </Descriptions.Item>

                  {adjacentCrop.comment_closed && (
                    <Descriptions.Item 
                      label={t("adjacent_crops.field.comment_closed", "Comentario de Cierre")} 
                      span={2}
                    >
                      {adjacentCrop.comment_closed}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}
          </Card>

          {/* Review History Timeline */}
          <Card
            title={
              <Space>
                <HistoryOutlined />
                {t("adjacent_crops.review_history", "Histórico de Revisiones")}
              </Space>
            }
          >
            {sortedReviews.length > 0 ? (
              <Timeline mode="left">
                {sortedReviews.map((review) => (
                  <Timeline.Item
                    key={review.id}
                    color={review.is_first_review ? "green" : "blue"}
                    label={
                      <Space direction="vertical" size={0}>
                        <Text strong>
                          {dayjs(review.audit_date).format("DD/MM/YYYY")}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {review.audit_reference}
                        </Text>
                      </Space>
                    }
                  >
                    <Card
                      size="small"
                      style={{ marginBottom: 16 }}
                      extra={
                        review.is_first_review && (
                          <Tag color="green">
                            {t("adjacent_crops.first_review", "Primera Revisión")}
                          </Tag>
                        )
                      }
                    >
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        {/* Review Details */}
                        <Row gutter={[16, 8]}>
                          <Col span={12}>
                            <Space>
                              <FieldTimeOutlined />
                              <Text type="secondary">
                                {t("adjacent_crops.field.surface", "Superficie")}:
                              </Text>
                              <Text strong>{review.estimated_surface?.toFixed(2)} ha</Text>
                            </Space>
                          </Col>
                          <Col span={12}>
                            <Space>
                              <InfoCircleOutlined />
                              <Text type="secondary">
                                {t("adjacent_crops.field.species", "Especie")}:
                              </Text>
                              <Text>{review.species_name}</Text>
                            </Space>
                          </Col>
                          {review.variety_name && (
                            <Col span={12}>
                              <Space>
                                <InfoCircleOutlined />
                                <Text type="secondary">
                                  {t("adjacent_crops.field.variety", "Variedad")}:
                                </Text>
                                <Text>{review.variety_name}</Text>
                              </Space>
                            </Col>
                          )}
                        </Row>

                        {/* Comment */}
                        {review.comment && (
                          <>
                            <Divider style={{ margin: "8px 0" }} />
                            <Paragraph style={{ marginBottom: 0 }}>
                              <Text type="secondary">
                                {t("adjacent_crops.field.comment", "Comentario")}:
                              </Text>
                              <br />
                              {review.comment}
                            </Paragraph>
                          </>
                        )}

                        {/* Photos */}
                        {review.photos && review.photos.length > 0 && (
                          <>
                            <Divider style={{ margin: "8px 0" }} />
                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                              <Text type="secondary">
                                <PictureOutlined /> {t("adjacent_crops.photos", "Fotos")} ({review.photos.length})
                              </Text>
                              <Image.PreviewGroup>
                                <Row gutter={[8, 8]}>
                                  {review.photos.map((photo) => (
                                    <Col key={photo.id} xs={12} sm={8} md={6} lg={4}>
                                      <Image
                                        src={photo.image_url || undefined}
                                        alt={photo.description || `Foto ${review.audit_reference}`}
                                        style={{
                                          width: "100%",
                                          height: 100,
                                          objectFit: "cover",
                                          borderRadius: 4,
                                        }}
                                        placeholder={
                                          <div
                                            style={{
                                              width: "100%",
                                              height: 100,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              background: "#f0f0f0",
                                            }}
                                          >
                                            <PictureOutlined style={{ fontSize: 24 }} />
                                          </div>
                                        }
                                      />
                                    </Col>
                                  ))}
                                </Row>
                              </Image.PreviewGroup>
                            </Space>
                          </>
                        )}
                      </Space>
                    </Card>
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <Empty description={t("adjacent_crops.no_reviews", "No hay revisiones registradas")} />
            )}
          </Card>

          {/* All Photos Gallery */}
          {allPhotos.length > 0 && (
            <Card
              title={
                <Space>
                  <PictureOutlined />
                  {t("adjacent_crops.all_photos", "Todas las Fotos")} ({allPhotos.length})
                </Space>
              }
            >
              <Image.PreviewGroup>
                <Row gutter={[16, 16]}>
                  {allPhotos.map((photo) => (
                    <Col key={photo.id} xs={12} sm={8} md={6} lg={4}>
                      <div style={{ position: "relative" }}>
                        <Image
                          src={photo.image_url || undefined}
                          alt={photo.description || `Foto ${photo.audit_reference}`}
                          style={{
                            width: "100%",
                            height: 150,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                          placeholder={
                            <div
                              style={{
                                width: "100%",
                                height: 150,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#f0f0f0",
                              }}
                            >
                              <PictureOutlined style={{ fontSize: 32 }} />
                            </div>
                          }
                        />
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: "rgba(0, 0, 0, 0.6)",
                            color: "white",
                            padding: "4px 8px",
                            fontSize: 11,
                            borderBottomLeftRadius: 8,
                            borderBottomRightRadius: 8,
                          }}
                        >
                          <div>{photo.audit_reference}</div>
                          <div>{dayjs(photo.audit_date).format("DD/MM/YYYY")}</div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Image.PreviewGroup>
            </Card>
          )}

          {/* Location Map - Placeholder for future implementation */}
          {adjacentCrop.farm && (
            <Card
              title={
                <Space>
                  <EnvironmentOutlined />
                  {t("adjacent_crops.location", "Ubicación")}
                </Space>
              }
            >
              <Empty
                description={t("adjacent_crops.map_coming_soon", "Mapa próximamente disponible")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          )}
        </Space>
      </Show>
    </div>
  );
};

export default AdjacentCropShow;
